import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    ContactUsDTO,
    useDeleteContactUsApi,
    useSaveContactUsApi,
    useSearchContactUsApi,
} from '../../../api/contact-us-api';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveContactUsModal } from './save-contact-us-modal';

export function ContactUsApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedContactUs, setSelectedContactUs] = useState<ContactUsDTO | undefined>();
    const contactUsInit = useSearchContactUsApi();
    const saveContactUs = useSaveContactUsApi();
    const deleteContactUs = useDeleteContactUsApi();

    const { result, error, initialized, initializing } = contactUsInit;

    const columns: IBasicTableColumn<ContactUsDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 40,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>subject</T>,
                name: 'subject',
            },
            {
                Header: <T>phone</T>,
                name: 'phone',
                width: 180,
            },
            {
                Header: <T>email</T>,
                name: 'email',
                width: 180,
            },

            {
                Header: <T>created-by</T>,
                name: 'createdBy',
                width: 100,
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        contactUsInit.post(null, {});
    }, [initialized, initializing, error, contactUsInit]);

    const handleDelete = (contactUs: ContactUsDTO) => {
        bd.msgbox(
            translate('delete-contact-us'),
            translate('confirm-delete-contact-us'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteContactUs.remove(
                            contactUs,
                            () => {
                                contactUsInit.updateResult({
                                    ...result,
                                    contactuses: result.contactuses.filter(
                                        b => b.id !== contactUs.id
                                    ),
                                });
                                notify.dark(translate('selected-contact-us-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedContactUs(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (contactUs: ContactUsDTO) => {
        saveContactUs.post(
            null,
            contactUs,
            res => {
                if (selectedContactUs) {
                    contactUsInit.updateResult({
                        ...result,
                        contactuses: result.contactuses.map(b =>
                            b.id === contactUs.id ? res.contactUs : b
                        ),
                    });
                } else {
                    contactUsInit.updateResult({
                        ...result,
                        contactuses: [res.contactUs, ...result.contactuses],
                    });
                }

                setShowModal(false);
                setSelectedContactUs(undefined);
                notify.success(
                    selectedContactUs
                        ? translate('selected-contact-us-updated-successfully')
                        : translate('new-contact-us-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => contactUsInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar onGoBack="/home" title="contact-us" />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.contactuses}
                        onRowClick={({ row }) => {
                            setSelectedContactUs(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveContactUsModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedContactUs(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedContactUs={selectedContactUs}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
